import storage from 'utils/storage';

// ============================================================================
// ChunkLoadError Recovery Utilities
// ============================================================================
// Centralized handling for webpack chunk loading failures (JS and CSS chunks).
// Used by both:
// - index.tsx: Global unhandledrejection handler for async chunk failures
// - app.tsx: React ErrorBoundary for sync chunk failures during rendering
// ============================================================================

/** Storage key for chunk failure cooldown tracking */
export const CHUNK_ERROR_STORAGE_KEY = 'chunk_failed';

/** Cooldown in milliseconds to prevent infinite reload loops */
export const CHUNK_ERROR_COOLDOWN_MS = 10000;

/**
 * Checks if an error is a chunk loading error from webpack.
 * Handles both JS and CSS chunk failures.
 */
export function isChunkLoadError(error: Error | null | undefined): boolean {
  if (!error) return false;
  return error.name === 'ChunkLoadError' || Boolean(error.message) && (error.message.includes('Loading chunk') && error.message.includes('failed') || error.message.includes('Loading CSS chunk') && error.message.includes('failed'));
}

/**
 * Checks if an error indicates the server returned HTML instead of a JS chunk.
 * This happens when the server serves a maintenance/fallback page (e.g., index.html)
 * instead of the requested JavaScript file. The browser tries to parse HTML as JS
 * and fails with "Unexpected token '<'" or similar.
 */
export function isHtmlFallbackError(error: Error | null | undefined): boolean {
  if (!error?.message) return false;
  return error.message.includes('Unexpected token') || error.message.includes('Unexpected identifier');
}

/**
 * Checks if the error is recoverable by reloading the page.
 * Combines chunk load errors and HTML fallback errors.
 */
export function isRecoverableLoadError(error: Error | null | undefined): boolean {
  return isChunkLoadError(error) || isHtmlFallbackError(error);
}

/**
 * Handles chunk load errors by reloading the page with cooldown protection.
 * Returns true if reload was triggered, false if cooldown prevented it.
 */
export function handleChunkLoadError(): boolean {
  if (!storage.getWithExpiry(CHUNK_ERROR_STORAGE_KEY)) {
    storage.putWithExpiry(CHUNK_ERROR_STORAGE_KEY, 'true', CHUNK_ERROR_COOLDOWN_MS);
    window.location.reload();
    return true;
  }
  return false;
}